import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";
import { createRestSlice, ItemState } from "./restSlice";

export type DeploymentSTTConfig = {
  // Core STT
  sttModel: string; // e.g. "nova-3", "deepgram-flux"
  sttLanguage?: string; // "en", "zh", "multi"
  sttPrompt?: string; // Keywords or context prompt

  // Turn Detection & Timing
  sttEndpointing?: number; // Silence detection (ms)
  eotThreshold?: number; // End-of-turn confidence (0.5-0.9)
  eotTimeoutMs?: number; // Max silence timeout (ms)
  eagerEotThreshold?: number; // Early turn detection

  // VAD (Voice Activity Detection)
  vadMode?: "server_vad" | "semantic_vad";
  vadEagerness?: "auto" | "low" | "medium" | "high";

  // Audio Processing
  sttNoiseReductionType?: string; // "near_field" | "far_field"
  sttFilter?: string; // "dtln" | "deepfilternet2" | "krisp"
  audioInputDelay?: number; // Processing delay (ms)

  // Advanced Features
  sttAutoSwitch?: boolean; // Auto language switching
  sttPrewarmModel?: string; // Background model preloading

  // Environment Settings
  moduleRule?: string; // Module execution rule
  isDebug?: boolean; // Debug mode

  // Error Fallback Configuration
  errorFallbackType?: string; // "phone" | "webhook" | etc.
  errorFallbackValue?: string; // Phone number or webhook URL
  errorFallbackTimeout?: number; // Timeout in ms
};

export type AgentDeployment = {
  id: string;
  agentId: string;
  environmentId: string;
  functionId?: string; // Function that was deployed (historical snapshot)

  // Deployment metadata
  deployedAt: string; // ISO date string
  deployedBy: string; // User ID who deployed
  deploymentType: string[];

  // STT Configuration Snapshot
  sttConfig: DeploymentSTTConfig;

  // General environment variables (non-STT)
  environmentVariables: Record<string, string>;

  // Status
  status: "pending" | "deployed" | "failed";
  isActive: boolean;
  isDedicated: boolean;

  // Standard timestamps
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

const agentDeploymentSlice = createRestSlice<AgentDeployment>("agentDeployments", "/v1/agents/:agentId/deployments");

export const agentDeploymentReducer = agentDeploymentSlice.reducer;
export const agentDeploymentActions = agentDeploymentSlice.actions;
